import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/core/api/client";
import { useSession } from "@/core/auth/useSession";
import { Alert, Badge, Button, Card, CardTitle, Field, Input, useToast } from "@/core/ui";
import {
  useAddMember,
  useCreateTeam,
  useRemoveMember,
  useRenameTeam,
  useTeams,
  type Team,
} from "./api";

export function TeamsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const teams = useTeams();
  const create = useCreateTeam();
  const [name, setName] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim() },
      {
        onSuccess: () => {
          setName("");
          toast(t("teams.created"), "success");
        },
        onError: () => toast(t("app.error_generic"), "danger"),
      },
    );
  };

  return (
    <section className="bt-teams">
      <h1 className="bt-page-title">{t("teams.title")}</h1>
      <Alert tone="info">{t("teams.explainer")}</Alert>

      <Card>
        <CardTitle>{t("teams.create")}</CardTitle>
        <form onSubmit={submit} className="bt-inline-form">
          <div className="bt-inline-form__field bt-inline-form__field--wide">
            <Field label={t("teams.name")} htmlFor="team-name">
              <Input id="team-name" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
          <Button type="submit" className="bt-inline-form__submit" loading={create.isPending}>
            {t("teams.create_action")}
          </Button>
        </form>
      </Card>

      {teams.isPending ? <p className="bt-page-message">{t("app.loading")}</p> : null}
      {teams.data && teams.data.results.length === 0 ? (
        <p className="bt-page-message">{t("teams.empty")}</p>
      ) : null}
      <ul className="bt-teams__grid">
        {(teams.data?.results ?? []).map((team) => (
          <li key={team.id}>
            <TeamCard team={team} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function TeamCard({ team }: { team: Team }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const session = useSession();
  const rename = useRenameTeam();
  const addMember = useAddMember();
  const removeMember = useRemoveMember();
  const [email, setEmail] = useState("");
  const [newName, setNewName] = useState(team.name);
  const [memberError, setMemberError] = useState<string | null>(null);
  const myId = session.data?.user?.id ?? null;

  const invite = (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    setMemberError(null);
    addMember.mutate(
      { id: team.id, email: email.trim() },
      {
        onSuccess: () => {
          setEmail("");
          toast(t("teams.member_added"), "success");
        },
        onError: (error) =>
          setMemberError(
            error instanceof ApiError && error.code === "user_not_found"
              ? "user_not_found"
              : "generic",
          ),
      },
    );
  };

  return (
    <Card className="bt-team-card">
      <div className="bt-team-card__header">
        <CardTitle className="bt-card__title--flush">{team.name}</CardTitle>
        {team.is_captain ? <Badge tone="success">{t("teams.captain")}</Badge> : null}
        {team.is_member && !team.is_captain ? <Badge tone="info">{t("teams.member")}</Badge> : null}
      </div>

      <ul className="bt-team-card__members">
        {team.members.map((member) => (
          <li key={member.id} className="bt-team-card__member">
            <Badge tone="neutral">{member.public_name}</Badge>
            {team.is_captain && member.id !== myId ? (
              <button
                type="button"
                className="bt-team-card__remove"
                onClick={() => removeMember.mutate({ id: team.id, userId: member.id })}
              >
                {t("teams.remove")}
              </button>
            ) : null}
          </li>
        ))}
      </ul>

      {team.is_captain ? (
        <>
          <form onSubmit={invite} className="bt-inline-form">
            <div className="bt-inline-form__field">
              <Field label={t("teams.invite")} htmlFor={`invite-${team.id}`}>
                <Input
                  id={`invite-${team.id}`}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
            </div>
            <Button
              type="submit"
              variant="secondary"
              className="bt-inline-form__submit"
              loading={addMember.isPending}
            >
              {t("admin.add")}
            </Button>
          </form>
          <div aria-live="polite">
            {memberError ? (
              <Alert tone="danger">
                {memberError === "user_not_found"
                  ? t("teams.user_not_found")
                  : t("app.error_generic")}
              </Alert>
            ) : null}
          </div>
          <form
            className="bt-inline-form bt-team-card__rename"
            onSubmit={(event) => {
              event.preventDefault();
              rename.mutate({ id: team.id, name: newName.trim() });
            }}
          >
            <div className="bt-inline-form__field">
              <Field label={t("teams.rename")} htmlFor={`rename-${team.id}`}>
                <Input
                  id={`rename-${team.id}`}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </Field>
            </div>
            <Button
              type="submit"
              variant="outline"
              className="bt-inline-form__submit"
              loading={rename.isPending}
            >
              {t("admin.save")}
            </Button>
          </form>
        </>
      ) : null}

      {team.is_member && myId !== null ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => removeMember.mutate({ id: team.id, userId: myId })}
          loading={removeMember.isPending}
        >
          {t("teams.leave")}
        </Button>
      ) : null}
    </Card>
  );
}
